// Las preguntas que preceden a una descarga, en un solo lugar —
// ADR-2026-09-14-configuracion-de-descarga.
//
// Reemplaza a `useWatermarkDownload` y mantiene su contrato: la superficie monta
// `chooserElement`, pregunta UNA vez por operación con `resolverOpciones()` y pasa cada
// blob de imagen por `aplicarOpciones` antes de escribirlo. Lo que cambia es que ahora
// las preguntas son dos (marca de agua y formato de salida) y se hacen juntas.
//
// Si no hay nada que preguntar —ni marca de agua activa ni formato configurado—
// resuelve al toque y sin UI: una empresa que no configuró nada descarga exactamente
// como antes de que esto existiera.
//
// El ORDEN de las transformaciones importa: primero la marca de agua sobre el original,
// después el recorte y el re-encode. Al revés, la marca quedaría dibujada sobre la
// imagen chica y se recomprimiría dos veces; así, la marca se compone sobre el original
// y el único encode que decide el peso final es el último.

import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useBrandProfile } from '../../hooks/useBrandProfile';
import { applyWatermarkToBlob } from '../../utils/watermark';
import {
  fetchWatermarkConfig,
  getLastWatermarkChoice,
  setLastWatermarkChoice,
} from '../../utils/watermarkConfig';
import {
  ORIGINAL,
  fetchConfigDeDescarga,
  getUltimaEleccionDeFormato,
  modoDeDescarga,
  perfilElegido,
  setUltimaEleccionDeFormato,
  type ConfigDeDescarga,
} from '../../utils/configDeDescarga';
import { convertirImagen } from '../../utils/formatoDeSalida';
import { formatoPersonalizadoDeDatos } from '../../utils/formatoPersonalizado';
import {
  OpcionesDeDescargaChooser,
  type OpcionesDeDescarga,
} from './OpcionesDeDescargaChooser';

export type { OpcionesDeDescarga };

/**
 * Bajar el original tal cual. Es lo que se resuelve cuando no hay nada configurado y,
 * exportado, lo que pasan las superficies cuando el archivo es un VIDEO: ni la marca de
 * agua ni el formato de salida se componen sobre un mp4, así que ni se pregunta.
 */
export const OPCIONES_SIN_TOCAR: OpcionesDeDescarga = { marcaDeAgua: 'sin', formato: ORIGINAL };

export interface OpcionesDeLaPregunta {
  /** Cuántos archivos se van a bajar: cambia el título de la hoja y nada más. */
  cantidad?: number;
  /**
   * Este material NO se marca con la marca de agua, así que tampoco se pregunta.
   *
   * Lo usa la galería de guardados del chat: ahí lo que se baja son las fotos de
   * PRODUCTO, los fondos y los modelos, o sea el material con el que se genera. Una
   * marca de agua sobre una foto de referencia no le sirve a nadie, y preguntar por
   * ella sería estrenar una pregunta en un flujo que nunca la tuvo.
   */
  sinMarcaDeAgua?: boolean;
}

interface EstadoDelChooser {
  preguntarMarcaDeAgua: boolean;
  configDeFormato: ConfigDeDescarga | null;
  inicial: OpcionesDeDescarga;
  cantidad?: number;
}

export function useOpcionesDeDescarga(): {
  chooserElement: ReactNode;
  /** `null` = el usuario cerró la hoja, o sea canceló la descarga. */
  resolverOpciones: (opts?: OpcionesDeLaPregunta) => Promise<OpcionesDeDescarga | null>;
  /**
   * `datosGeneracion`: el `datos_generacion` de la fila, si quien baja lo tiene. Una imagen
   * generada con un formato PERSONALIZADO lleva ahí su copia y sale recortada y convertida
   * a eso sin preguntar (ADR-2026-10-20-formatos-personalizados), salvo que el usuario haya
   * elegido otro formato en la hoja.
   */
  aplicarOpciones: (
    blob: Blob,
    esVideo: boolean,
    opciones: OpcionesDeDescarga | null,
    datosGeneracion?: unknown,
  ) => Promise<Blob>;
} {
  const [estado, setEstado] = useState<EstadoDelChooser | null>(null);
  const resolverRef = useRef<((o: OpcionesDeDescarga | null) => void) | null>(null);
  const { activeBrand } = useBrandProfile();
  const brandId = activeBrand?.id ?? null;

  const resolverOpciones = useCallback(
    async ({ cantidad, sinMarcaDeAgua }: OpcionesDeLaPregunta = {}): Promise<
      OpcionesDeDescarga | null
    > => {
      if (!brandId) return OPCIONES_SIN_TOCAR;
      const [marca, descarga] = await Promise.all([
        sinMarcaDeAgua ? null : fetchWatermarkConfig(brandId),
        fetchConfigDeDescarga(brandId),
      ]);
      const preguntarMarcaDeAgua = !!marca?.enabled;
      const modo = modoDeDescarga(descarga);

      // Nada que preguntar: se resuelve sin abrir nada. `'perfil'` acá es el caso de
      // quien prendió "siempre" — sale convertida y en silencio, que es lo que pidió.
      // Con "siempre" la elección ya está hecha: es ese perfil.
      const fijo = modo === 'perfil' ? descarga!.siempre! : null;
      if (!preguntarMarcaDeAgua && modo !== 'preguntar') {
        return { marcaDeAgua: 'sin', formato: fijo ?? ORIGINAL };
      }
      // La última elección de este dispositivo puede señalar un perfil que ya no existe.
      const ultima = getUltimaEleccionDeFormato();
      const recordada = perfilElegido(descarga, ultima) ? ultima : ORIGINAL;

      return new Promise((resolve) => {
        // UNA hoja a la vez. Un segundo pedido mientras la primera sigue abierta pisaba
        // el resolver anterior y esa promesa no se cumplía NUNCA: el botón que la estaba
        // esperando se quedaba colgado para siempre. El camino natural es el doble click,
        // porque todos los callers prenden su estado de "descargando" DESPUÉS de esperar
        // esta respuesta, así que el segundo click entra sin que nada lo frene.
        //
        // El duplicado se cancela con `null` —que es exactamente lo que los siete callers
        // ya tratan como "no descargues"— y la hoja abierta sigue siendo la del primero:
        // un solo pedido, una sola descarga, sin reabrir ni parpadear la hoja.
        if (resolverRef.current) {
          resolve(null);
          return;
        }
        resolverRef.current = resolve;
        setEstado({
          preguntarMarcaDeAgua,
          // Con "siempre" prendido el formato no se pregunta aunque se abra la hoja
          // por la marca de agua: ya está decidido.
          configDeFormato: modo === 'preguntar' ? descarga : null,
          inicial: {
            marcaDeAgua: getLastWatermarkChoice(),
            formato: fijo ?? recordada,
          },
          cantidad,
        });
      });
    },
    [brandId],
  );

  const aplicarOpciones = useCallback(
    async (
      blob: Blob,
      esVideo: boolean,
      opciones: OpcionesDeDescarga | null,
      datosGeneracion?: unknown,
    ): Promise<Blob> => {
      // Los videos pasan de largo enteros: ni la marca de agua ni el recorte se
      // componen sobre un mp4 en el navegador.
      if (!opciones || esVideo || !blob.type.startsWith('image/')) return blob;
      let salida = blob;

      if (opciones.marcaDeAgua === 'con' && brandId) {
        const config = await fetchWatermarkConfig(brandId);
        // Config activa pero sin capas (no debería pasar: guardar vacío desactiva) —
        // re-encodear la imagen para no marcar nada sería puro costo.
        if (config?.enabled && (config.logo.visible || config.text.visible)) {
          try {
            salida = await applyWatermarkToBlob(salida, config, activeBrand?.logo_url ?? null);
          } catch (err) {
            console.warn('descarga: no se pudo aplicar la marca de agua', err);
          }
        }
      }

      if (opciones.formato !== ORIGINAL && brandId) {
        const config = await fetchConfigDeDescarga(brandId);
        // Si el perfil ya no está en la lista, no se convierte: mejor el original que
        // adivinar. `convertirImagen` ya devuelve el original ante cualquier falla.
        const perfil = config?.activo ? perfilElegido(config, opciones.formato) : null;
        if (perfil) {
          salida = await convertirImagen(salida, {
            ancho: perfil.ancho,
            alto: perfil.alto,
            formato: perfil.formato,
            pesoMaxKB: perfil.pesoMaxKB,
          });
        }
        return salida;
      }

      // Sin elección en la hoja, la imagen que se generó PARA un formato personalizado sale
      // en ese formato: es lo que se pidió al generar. La copia viaja en la fila, así que
      // vale aunque el formato se haya borrado de la marca.
      const propio = formatoPersonalizadoDeDatos(datosGeneracion);
      if (propio) {
        salida = await convertirImagen(salida, {
          ancho: propio.ancho,
          alto: propio.alto,
          formato: propio.formato,
          pesoMaxKB: propio.pesoMaxKB,
        });
      }

      return salida;
    },
    [activeBrand?.logo_url, brandId],
  );

  const cerrar = (o: OpcionesDeDescarga | null) => {
    resolverRef.current?.(o);
    resolverRef.current = null;
    setEstado(null);
  };

  const chooserElement = (
    <OpcionesDeDescargaChooser
      open={estado !== null}
      preguntarMarcaDeAgua={estado?.preguntarMarcaDeAgua ?? false}
      configDeFormato={estado?.configDeFormato ?? null}
      inicial={estado?.inicial ?? OPCIONES_SIN_TOCAR}
      cantidad={estado?.cantidad}
      onConfirmar={(o) => {
        setLastWatermarkChoice(o.marcaDeAgua);
        if (estado?.configDeFormato) setUltimaEleccionDeFormato(o.formato);
        cerrar(o);
      }}
      onClose={() => cerrar(null)}
    />
  );

  return { chooserElement, resolverOpciones, aplicarOpciones };
}
